import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { RiFileExcel2Line } from "react-icons/ri";

// import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

const StatsByStoreAndDivisionTable = ({ statsByDivision }) => {
  const handleDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Stats by Store");

    // Define header and subheader rows
    const headerRow1 = ["Stats by Store and Division"];
    const headerRow2 = ["Store", "Women's", "", "Men's", ""];
    const headerRow3 = ["", "Group", "Count", "Group", "Count"];

    // Add headers to the worksheet
    worksheet.addRow(headerRow1);
    worksheet.addRow(headerRow2);
    worksheet.addRow(headerRow3);

    // Merge cells for title and grouped headers
    worksheet.mergeCells("A1:E1"); // Merge title row
    worksheet.mergeCells("B2:C2"); // Merge Women's Group and Count
    worksheet.mergeCells("D2:E2"); // Merge Men's Group and Count

    // Apply styles to the title
    const titleCell = worksheet.getCell("A1");
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    titleCell.font = { bold: true, color: { argb: "0000FF"} };

    // Apply styles to headers
    worksheet.getRow(2).eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFDA" },
      }; // Light green fill
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    worksheet.getRow(3).eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FCE4D6" },
      }; // Light orange fill
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Add rows for data
    statsByDivision.data
      .filter((d) => d.store !== "ECOMMERCE")
      .forEach((d, index) => {
        const row = worksheet.addRow([
          d.store,
          d.womensGroup,
          d.womensCount,
          d.mensGroup,
          d.mensCount,
        ]);

        // Apply alternating row colors
        const isEvenRow = index % 2 === 0;
        row.eachCell((cell, colIndex) => {
          cell.alignment = {
            horizontal: colIndex === 1 ? "left" : "center", // Store column is left-aligned
            vertical: "middle",
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: isEvenRow ? "BDD7EE" : "FFFFFF" }, // Alternating row colors
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

    // Adjust column widths
    worksheet.columns = [
      { width: 20 }, // Store column
      { width: 15 }, // Women's Group
      { width: 10 }, // Women's Count
      { width: 15 }, // Men's Group
      { width: 10 }, // Men's Count
    ];

    // Generate the Excel file and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Stats_by_Store_and_Division.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Grid item xs={6}>
      <Paper elevation={3} sx={{ padding: "20px", height: 462 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "16" }}>
              {"Stats by Store and Division"}
            </Typography>
            <Tooltip
              title={"Summarizes men's and women's product counts by store."}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Box>
          <Tooltip color="success" title="Download Excel">
            <IconButton color="success" onClick={handleDownload}>
              <RiFileExcel2Line />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <DataGrid
            rows={statsByDivision.data
              .filter((d) => d.store !== "ECOMMERCE")
              .map((d, index) => ({
                id: index,
                store: d.store,
                womensGroup: d.womensGroup,
                womensCount: d.womensCount,
                mensGroup: d.mensGroup,
                mensCount: d.mensCount,
              }))}
            showCellVerticalBorder={true}
            columns={[
              {
                field: "store",
                headerName: "Store",
                flex: 1,
                headerAlign: "center",
                align: "left",
                renderHeader: () => (
                  <Typography sx={{ fontWeight: "bold", textAlign: "left" }}>
                    Store
                  </Typography>
                ),
              },
              {
                field: "womensGroup",
                headerName: "Women's",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderHeader: () => (
                  <Box>
                    <Typography sx={{ fontWeight: "bold" }}>Women's</Typography>
                    <Typography>Group</Typography>
                  </Box>
                ),
              },
              {
                field: "womensCount",
                headerName: "",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderHeader: () => (
                  <Typography sx={{ fontWeight: "bold" }}>Count</Typography>
                ),
              },
              {
                field: "mensGroup",
                headerName: "Men's",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderHeader: () => (
                  <Box>
                    <Typography sx={{ fontWeight: "bold" }}>Men's</Typography>
                    <Typography>Group</Typography>
                  </Box>
                ),
              },
              {
                field: "mensCount",
                headerName: "",
                flex: 1,
                headerAlign: "center",
                align: "center",
                renderHeader: () => (
                  <Typography sx={{ fontWeight: "bold" }}>Count</Typography>
                ),
              },
            ]}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "table-row-even"
                : "table-row-odd"
            }
            pageSize={10}
            rowsPerPageOptions={[5, 10]}
            disableSelectionOnClick
            rowHeight={20}
            hideFooter
          />
        </Box>
      </Paper>
    </Grid>
  );
};

export default StatsByStoreAndDivisionTable;
